(function () {
  const DESKTOP_MQ = window.matchMedia('(min-width: 64rem)');
  const BACKDROP_CLASS = 'collection-filter-backdrop';

  let globalsBound = false;
  let stickyRafId = 0;
  /** @type {ResizeObserver | null} */
  let measureObserver = null;
  /** @type {WeakSet<Element> | null} */
  const observedMeasures = typeof WeakSet !== 'undefined' ? new WeakSet() : null;

  function getHeaderMeasure() {
    const bar = document.querySelector('.site-header__bar');
    if (bar instanceof HTMLElement) return bar;
    const sticky = document.querySelector('.site-header__sticky');
    return sticky instanceof HTMLElement ? sticky : null;
  }

  /**
   * The page scrolls normally; only the filter card is sticky. Keep its sticky
   * offset (--collection-filter-sticky-top) matched to the live header bottom,
   * which varies with the promo bar / collapsed 72px bar.
   */
  function syncStickyOffset() {
    if (!document.querySelector('[data-collection-page]')) return;

    const measure = getHeaderMeasure();
    if (!measure) return;

    const edge = Math.max(0, Math.ceil(measure.getBoundingClientRect().bottom));

    if (DESKTOP_MQ.matches) {
      document.documentElement.style.setProperty('--collection-filter-sticky-top', `${edge}px`);
      document.documentElement.style.removeProperty('--collection-filter-sheet-top');
      return;
    }

    document.documentElement.style.removeProperty('--collection-filter-sticky-top');
    document.documentElement.style.setProperty('--collection-filter-sheet-top', `${edge}px`);
  }

  function scheduleStickySync() {
    if (stickyRafId) return;
    stickyRafId = window.requestAnimationFrame(() => {
      stickyRafId = 0;
      syncStickyOffset();
    });
  }

  function observeHeaderMeasure() {
    if (typeof ResizeObserver === 'undefined') return;

    const measure = getHeaderMeasure();
    if (!measure || observedMeasures?.has(measure)) return;

    if (!measureObserver) measureObserver = new ResizeObserver(scheduleStickySync);
    measureObserver.observe(measure);
    observedMeasures?.add(measure);
  }

  function ensureBackdrop() {
    let backdrop = document.querySelector(`.${BACKDROP_CLASS}`);
    if (!(backdrop instanceof HTMLElement)) {
      backdrop = document.createElement('button');
      backdrop.type = 'button';
      backdrop.className = BACKDROP_CLASS;
      backdrop.hidden = true;
      const slot = document.querySelector('[data-collection-filter-slot]');
      backdrop.setAttribute(
        'aria-label',
        (slot instanceof HTMLElement && slot.dataset.closeFiltersLabel) || 'Close filters'
      );
      backdrop.addEventListener('click', () => setFilterOpen(false));
      document.body.appendChild(backdrop);
    }
    return backdrop;
  }

  /** @param {boolean} open */
  function setFilterOpen(open) {
    const slot = document.querySelector('[data-collection-filter-slot]');
    if (!(slot instanceof HTMLElement)) return;

    slot.classList.toggle('is-open', open);
    ensureBackdrop().hidden = !open;
    document.body.classList.toggle('collection-filter-open', open);
    if (open) syncStickyOffset();

    document.querySelectorAll('[data-collection-filter-open]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(open));
    });

    if (open) {
      const closeBtn = slot.querySelector('[data-collection-filter-close]');
      if (closeBtn instanceof HTMLElement) closeBtn.focus();
    }
  }

  /** Window/document listeners — bound once, resilient to section re-renders. */
  function bindGlobals() {
    if (globalsBound) return;
    globalsBound = true;

    window.addEventListener('load', syncStickyOffset);
    window.addEventListener('scroll', scheduleStickySync, { passive: true });
    window.addEventListener('resize', syncStickyOffset);
    DESKTOP_MQ.addEventListener('change', syncStickyOffset);
    document.addEventListener('map:header-stuck', scheduleStickySync);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && document.body.classList.contains('collection-filter-open')) {
        setFilterOpen(false);
      }
    });
  }

  /** @param {HTMLFormElement} form */
  function initSortForm(form) {
    if (form.dataset.sortInit === 'true') return;
    form.dataset.sortInit = 'true';

    const select = form.querySelector('[data-collection-sort-select]');
    if (!(select instanceof HTMLSelectElement)) return;

    const valueLabel = form.querySelector('[data-collection-sort-value]');

    function syncSortLabel() {
      if (!(valueLabel instanceof HTMLElement)) return;
      const option = select.options[select.selectedIndex];
      if (option) valueLabel.textContent = option.textContent.trim();
    }

    syncSortLabel();

    select.addEventListener('change', () => {
      syncSortLabel();
      // Navigate from the live URL rather than submitting the form: the form
      // only carries native filter params, so submitting wiped collections=,
      // tags=, price_min/max and the /+make_… vehicle path segment (WIL-326).
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      window.location.href = url.pathname + url.search;
    });
  }

  function initVehicleDrawer() {
    const drawer = document.getElementById('CollectionVehicleDrawer');
    if (!(drawer instanceof HTMLDialogElement)) return;

    if (drawer.dataset.vehicleInit === 'true') return;
    drawer.dataset.vehicleInit = 'true';

    const panel = drawer.querySelector('[data-vehicle-find-panel]');
    if (panel instanceof HTMLElement && window.MapUI?.initVehicleFindPanel) {
      window.MapUI.initVehicleFindPanel(panel);
    }
  }

  function initFilterToggles() {
    document.querySelectorAll('[data-collection-filter-open]').forEach((trigger) => {
      if (!(trigger instanceof HTMLElement) || trigger.dataset.filterToggleInit === 'true') return;
      trigger.dataset.filterToggleInit = 'true';

      trigger.addEventListener('click', () => {
        const slot = document.querySelector('[data-collection-filter-slot]');
        const isOpen = slot instanceof HTMLElement && slot.classList.contains('is-open');
        setFilterOpen(!isOpen);
      });
    });

    document.querySelectorAll('[data-collection-filter-close]').forEach((button) => {
      if (!(button instanceof HTMLElement) || button.dataset.filterCloseInit === 'true') return;
      button.dataset.filterCloseInit = 'true';
      button.addEventListener('click', () => setFilterOpen(false));
    });

    document.querySelectorAll('[data-collection-filter-vehicle]').forEach((button) => {
      if (!(button instanceof HTMLElement) || button.dataset.filterVehicleInit === 'true') return;
      button.dataset.filterVehicleInit = 'true';
      button.addEventListener('click', () => {
        setFilterOpen(false);
        const drawer = document.getElementById('CollectionVehicleDrawer');
        if (drawer instanceof HTMLDialogElement) window.MapUI?.openDrawer?.(drawer);
      });
    });
  }

  function boot() {
    if (!document.querySelector('[data-collection-page]')) return;

    bindGlobals();
    syncStickyOffset();
    observeHeaderMeasure();
    initVehicleDrawer();
    initFilterToggles();

    document.querySelectorAll('[data-collection-sort-form]').forEach((form) => {
      if (form instanceof HTMLFormElement) initSortForm(form);
    });

    initShowMore();
    initLocalShowMore();
    initVehicleHints();
  }

  let vehicleHintViewportBound = false;

  function placeVehicleHint(popover, button) {
    const rect = button.getBoundingClientRect();
    const gap = 8;
    const margin = 16;
    popover.style.left = '0px';
    popover.style.top = '0px';
    const pop = popover.getBoundingClientRect();
    let left = rect.left;
    let top = rect.bottom + gap;
    if (left + pop.width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - margin - pop.width);
    }
    if (top + pop.height > window.innerHeight - margin) {
      top = Math.max(margin, rect.top - gap - pop.height);
    }
    popover.style.left = `${Math.round(left)}px`;
    popover.style.top = `${Math.round(top)}px`;
  }

  function closeVehicleHints() {
    document.querySelectorAll('[data-vehicle-hint]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      const popover = document.getElementById(button.getAttribute('aria-controls') || '');
      if (!(popover instanceof HTMLElement) || !popover.matches(':popover-open')) return;
      if (typeof popover.hidePopover === 'function') popover.hidePopover();
    });
  }

  function initVehicleHints() {
    document.querySelectorAll('[data-vehicle-hint]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement) || button.dataset.vehicleHintInit === 'true') return;
      const popover = document.getElementById(button.getAttribute('aria-controls') || '');
      if (!(popover instanceof HTMLElement) || typeof popover.showPopover !== 'function') return;
      button.dataset.vehicleHintInit = 'true';

      popover.addEventListener('toggle', (event) => {
        const open = event instanceof ToggleEvent && event.newState === 'open';
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (!open) return;
        placeVehicleHint(popover, button);
        popover.focus({ preventScroll: true });
      });
    });

    if (vehicleHintViewportBound) return;
    vehicleHintViewportBound = true;
    window.addEventListener('resize', closeVehicleHints);
    window.addEventListener('scroll', closeVehicleHints, true);
  }

  function initShowMore() {
    document.querySelectorAll('[data-show-more]').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement) || link.dataset.showMoreInit === 'true') return;
      link.dataset.showMoreInit = 'true';

      link.addEventListener('click', async (event) => {
        event.preventDefault();
        if (link.getAttribute('aria-busy') === 'true') return;
        link.setAttribute('aria-busy', 'true');
        link.classList.add('is-loading');

        try {
          const response = await fetch(link.href, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('show more failed');
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const list = link.closest('.collection-page__product-list');
          const grid = list?.querySelector('.collection-page__grid');
          const nextGrid = doc.querySelector('.collection-page__product-list .collection-page__grid');
          if (!(grid instanceof HTMLElement) || !(nextGrid instanceof HTMLElement)) {
            throw new Error('missing grid');
          }

          nextGrid.querySelectorAll('.collection-page__grid-item:not([hidden])').forEach((item) => {
            grid.append(item);
          });

          const meta = list.querySelector('.collection-page__pagination-meta');
          const nextMeta = doc.querySelector('.collection-page__pagination-meta');
          if (meta instanceof HTMLElement && nextMeta instanceof HTMLElement) {
            meta.textContent = nextMeta.textContent;
          }

          const paginationNav = list.querySelector('.map-pagination');
          const nextPaginationNav = doc.querySelector(
            '.collection-page__product-list .map-pagination'
          );
          if (nextPaginationNav instanceof HTMLElement) {
            if (paginationNav instanceof HTMLElement) {
              paginationNav.replaceWith(nextPaginationNav);
            } else {
              const container = list.querySelector('.collection-page__pagination');
              if (container instanceof HTMLElement) {
                container.insertBefore(nextPaginationNav, meta ?? null);
              }
            }
          } else if (paginationNav instanceof HTMLElement) {
            paginationNav.remove();
          }

          const nextLink = doc.querySelector('[data-show-more]');
          if (nextLink instanceof HTMLAnchorElement) {
            link.href = nextLink.href;
          } else {
            link.remove();
          }

          window.MAPProductCard?.init();
        } catch {
          window.location.href = link.href;
        } finally {
          link.removeAttribute('aria-busy');
          link.classList.remove('is-loading');
        }
      });
    });
  }

  function initLocalShowMore() {
    document.querySelectorAll('[data-show-more-local]').forEach((button) => {
      if (!(button instanceof HTMLElement) || button.dataset.showMoreInit === 'true') return;
      button.dataset.showMoreInit = 'true';

      button.addEventListener('click', () => {
        const list = button.closest('.collection-page__product-list');
        if (!(list instanceof HTMLElement)) return;
        const hidden = [...list.querySelectorAll('[data-show-more-item][hidden]')];
        const meta = list.querySelector('[data-show-more-meta]');
        const pageSize = Number.parseInt(
          (meta instanceof HTMLElement && meta.dataset.pageSize) || '16',
          10
        );
        hidden.slice(0, pageSize).forEach((item) => {
          if (!(item instanceof HTMLElement)) return;
          item.hidden = false;
          delete item.dataset.showMoreItem;
        });
        if (hidden.length <= pageSize) button.remove();
        window.MAPProductCard?.init();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
